/* WP-10 activation (§14.5.6): token → OTP → terms/privacy → passkey offer →
 * welcome. Three short steps with step numbers. */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalApi, isPortalApiError } from '../api/portal-api';
import { useSession } from '../auth/session-context';
import { passkeyRegister, passkeySupported, toPasskeyError } from '../auth/passkey';
import { PrimaryButton, StepNumber, TextLink } from '../components/primitives';
import { SuccessTickIllustration } from '../illustrations';
import { mediaUrl, useAppearance } from '../appearance/appearance';
import { PORTAL_LOCALES, currentPortalLocale, setPortalLocale } from '../i18n';
import { errorText } from '../utils/error-text';
import { activationToken } from '../utils/activation-token';
import { useResendCooldown } from '../utils/use-resend-cooldown';

type Step = 'start' | 'code' | 'passkey' | 'done' | 'invalid';

type StartResponse = {
  activationTicket: string;
  channel: string;
  sentToMasked: string;
  otpExpiresAt: string;
  termsVersion: string;
  privacyNoticeVersion: string;
};

export function ActivatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const token = activationToken(window.location.search, window.location.hash);

  const [step, setStep] = useState<Step>('start');
  const [ticket, setTicket] = useState<string | null>(null);
  const [memberCode, setMemberCode] = useState('');
  const [otp, setOtp] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const { secondsLeft: resendSeconds, startCooldown } = useResendCooldown();

  const appearance = useAppearance();
  const heroUrl = mediaUrl(appearance?.document?.identity?.loginBackgroundAssetId);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await portalApi.post<StartResponse>('/auth/activation/start', { token });
      setTicket(res.activationTicket);
      setSentTo(res.sentToMasked);
      startCooldown();
      setStep('code');
    } catch (err) {
      if (
        isPortalApiError(err, 'PORTAL_INVITATION_INVALID') ||
        isPortalApiError(err, 'PORTAL_TICKET_INVALID')
      ) {
        setStep('invalid');
      } else {
        setError(errorText(err, t));
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!ticket) return;
    setBusy(true);
    setError(null);
    try {
      const res = await portalApi.post<StartResponse>('/auth/activation/resend', {
        activationTicket: ticket,
      });
      setSentTo(res.sentToMasked);
      startCooldown();
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await portalApi.post('/auth/activation/verify', {
        activationTicket: ticket,
        otp: otp.trim(),
        memberCode: memberCode.trim(),
        acceptTerms,
        acceptPrivacy,
        locale: currentPortalLocale(),
      });
      await refresh();
      setStep(passkeySupported() ? 'passkey' : 'done');
    } catch (err) {
      if (
        isPortalApiError(err, 'PORTAL_INVITATION_INVALID') ||
        isPortalApiError(err, 'PORTAL_TICKET_INVALID')
      ) {
        setStep('invalid');
      } else {
        setError(errorText(err, t));
      }
    } finally {
      setBusy(false);
    }
  };

  const enablePasskey = async () => {
    setBusy(true);
    try {
      await passkeyRegister();
      setStep('done');
    } catch (err) {
      setError(errorText(toPasskeyError(err), t));
    } finally {
      setBusy(false);
    }
  };

  if (!token || step === 'invalid') {
    return (
      <div className="hp-auth">
        <div className="hp-auth__body">
          <h1 className="hp-display-l">{t('auth.activate.title')}</h1>
          <p className="hp-muted">{t('auth.activate.tokenInvalid')}</p>
          <TextLink onClick={() => navigate('/login')}>{t('common.back')}</TextLink>
          <AuthFoot t={t} />
        </div>
      </div>
    );
  }

  return (
    <div className="hp-auth">
      {heroUrl ? (
        <div className="hp-auth__hero" aria-hidden>
          <img src={heroUrl} alt="" />
        </div>
      ) : null}
      <div className="hp-auth__body">
        {step === 'start' ? (
          <>
            <div>
              <h1 className="hp-display-xl">{t('auth.activate.title')}</h1>
              <p className="hp-muted">{t('auth.activate.subtitle')}</p>
            </div>
            <div className="hp-stack">
              <StepNumber n={1} label={t('auth.activate.step1')} />
              <StepNumber n={2} label={t('auth.activate.step2')} />
              <StepNumber n={3} label={t('auth.activate.step3')} />
            </div>
            <PrimaryButton onClick={start} loading={busy} data-quality-id="portal.activate.start">
              {t('auth.activate.start')}
            </PrimaryButton>
          </>
        ) : null}

        {step === 'code' ? (
          <form onSubmit={verify} className="hp-stack">
            <p className="hp-muted">
              {t('auth.activate.codeSent')} {sentTo}
            </p>
            <label className="hp-field">
              <span className="hp-field__label">{t('auth.login.memberCodeLabel')}</span>
              <input
                className="hp-input"
                value={memberCode}
                onChange={(e) => setMemberCode(e.target.value)}
                autoComplete="username"
                required
                data-quality-id="portal.activate.memberCode"
              />
            </label>
            <label className="hp-field">
              <span className="hp-field__label">{t('auth.login.codeLabel')}</span>
              <input
                className="hp-input"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                required
                data-quality-id="portal.activate.otp"
              />
            </label>
            <label className="hp-fact">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                required
              />
              <span>
                {t('auth.activate.acceptTerms')} (
                <a href="/legal/terms" target="_blank" rel="noreferrer">
                  {t('legal.terms')}
                </a>
                )
              </span>
            </label>
            <label className="hp-fact">
              <input
                type="checkbox"
                checked={acceptPrivacy}
                onChange={(e) => setAcceptPrivacy(e.target.checked)}
                required
              />
              <span>
                {t('auth.activate.acceptPrivacy')} (
                <a href="/legal/privacy" target="_blank" rel="noreferrer">
                  {t('legal.privacy')}
                </a>
                )
              </span>
            </label>
            <PrimaryButton loading={busy} type="submit" data-quality-id="portal.activate.verify">
              {t('auth.activate.verify')}
            </PrimaryButton>
            <TextLink
              chevron={false}
              onClick={resend}
              disabled={busy || resendSeconds > 0}
            >
              {resendSeconds > 0
                ? t('auth.activate.resendIn', { seconds: resendSeconds })
                : t('auth.activate.resend')}
            </TextLink>
          </form>
        ) : null}

        {step === 'passkey' ? (
          <>
            <div>
              <h1 className="hp-display-l">{t('auth.activate.passkeyOfferTitle')}</h1>
              <p className="hp-muted">{t('auth.activate.passkeyOfferBody')}</p>
            </div>
            <PrimaryButton
              onClick={enablePasskey}
              loading={busy}
              data-quality-id="portal.activate.passkey"
            >
              {t('auth.activate.passkeyOfferYes')}
            </PrimaryButton>
            <TextLink onClick={() => setStep('done')} chevron={false}>
              {t('auth.activate.passkeyOfferNo')}
            </TextLink>
          </>
        ) : null}

        {step === 'done' ? (
          <div className="hp-empty">
            <SuccessTickIllustration title={t('auth.activate.doneTitle')} />
            <h1 className="hp-empty__sentence">{t('auth.activate.doneTitle')}</h1>
            <p className="hp-muted">{t('auth.activate.doneBody')}</p>
            <PrimaryButton onClick={() => navigate('/')} data-quality-id="portal.activate.done">
              {t('auth.activate.doneCta')}
            </PrimaryButton>
          </div>
        ) : null}

        {error ? (
          <p className="hp-danger-text" role="alert">
            {error}
          </p>
        ) : null}

        <AuthFoot t={t} />
      </div>
    </div>
  );
}

/** Persistent help / legal / language entry on public activation screens. */
function AuthFoot({ t }: { t: (k: string) => string }) {
  return (
    <div className="hp-auth__foot">
      <Link to="/help">{t('me.help')}</Link>
      <Link to="/legal/terms">{t('legal.terms')}</Link>
      <Link to="/legal/privacy">{t('legal.privacy')}</Link>
      <span className="hp-auth__lang">
        {PORTAL_LOCALES.map((loc) => (
          <button
            key={loc}
            type="button"
            aria-pressed={currentPortalLocale() === loc}
            onClick={() => setPortalLocale(loc)}
          >
            {t(`me.languageNames.${loc}`)}
          </button>
        ))}
      </span>
    </div>
  );
}
